import { ArrayUtil } from "@mcleod/core";
import { Button, Layout, ReportPreview, SlideoutDecorator, Snackbar, TransitionOptions } from "@mcleod/components";
import { AutogenLayoutReprintFreightBill } from "./autogen/AutogenLayoutReprintFreightBill";

interface ReprintFreightBillProps {
    orderId: string;
    customerId: string;
    sendToImaging: boolean;
    invoiceNumber: string;
    isSummaryBill: boolean;
    billDate: Date;
    billType: string;
    isBalanceDue: boolean;
    includeOnlyInvoices: boolean;
    width?: string;
    transitionOptions?: Partial<TransitionOptions>;
}

export class ReprintFreightBill extends AutogenLayoutReprintFreightBill {
    private reportPreview: ReportPreview;
    public orderId: string;
    public customerId: string;
    public invoiceNumber: string;
    public sendToImaging: boolean = false;
    public isSummaryBill: boolean = false;
    public billDate: Date = null;
    public billType: string;
    public isBalanceDue: boolean = false;
    public includeOnlyInvoices: boolean = false;
    private _buttonConfirm = new Button({
        id: "buttonConfirm",
        caption: "Close",
        backgroundColor: "primary",
        color: "primary.reverse",
        minWidth: 128,
        borderWidth: 0,
        rowBreak: false,
        enabled: true,
        onClick: () => {
            this.slideOut();
        }
    });

    override onLoad() {
        this.createReportPreview();
    }

    private createReportPreview() {
        this.reportPreview = new ReportPreview(
            { reportPreviewTitle: ReprintFreightBill.getTitle(false) },
            { id: "reportPreview" });
        this.reportPreview.displaySingleResultInTabset = false;
        this.add(this.reportPreview);
    }

    public static openSlideout(props: ReprintFreightBillProps) {
        const reprintFreightBillLayout = Layout.getLayout("lme/ar/ReprintFreightBill") as ReprintFreightBill;
        const { orderId, customerId, sendToImaging, invoiceNumber, isSummaryBill, billDate, billType, isBalanceDue, includeOnlyInvoices } = props;
        new SlideoutDecorator({
            layout: reprintFreightBillLayout,
            fillVerticalSpace: true,
            width: props.width ?? "50%",
            title: ReprintFreightBill.getTitle(props.isBalanceDue),
            addlComponents: [reprintFreightBillLayout.buttonConfirm],
            transitionOptions: props.transitionOptions ?? null,
            doAfterSlideIn: async () => {
                reprintFreightBillLayout.orderId = orderId;
                reprintFreightBillLayout.customerId = customerId;
                reprintFreightBillLayout.sendToImaging = sendToImaging;
                reprintFreightBillLayout.invoiceNumber = invoiceNumber;
                reprintFreightBillLayout.isSummaryBill = isSummaryBill;
                reprintFreightBillLayout.billDate = billDate;
                reprintFreightBillLayout.billType = billType;
                reprintFreightBillLayout.isBalanceDue = isBalanceDue;
                reprintFreightBillLayout.includeOnlyInvoices = includeOnlyInvoices;
                await reprintFreightBillLayout.fetchInformation();
            }
        });
    }

    afterExecute() {
        this.displayReportData();
    }

    private displayReportData() {
        this.reportPreview.beginSearch();
        const responseFirstRow = ArrayUtil.getFirstElement(this.sourceReprintFreightBills.data);
        const reportName = ReprintFreightBill.getTitle(this.isBalanceDue);
        this.reportPreview.reportName = reportName;
        this.reportPreview.endSearch(responseFirstRow);
        if (this.reportPreview.isEmpty()) {
            Snackbar.showWarningSnackbar("No bills to print");
        }
    }

    public async fetchInformation() {
        this.buttonConfirm.busy = true;
        await this.sourceReprintFreightBills.search(
            {
                order_id: this.orderId,
                customer_id: this.customerId,
                send_to_imaging: this.sendToImaging,
                is_summary_bill: this.isSummaryBill,
                invoice_number: this.invoiceNumber,
                bill_date: this.billDate,
                bill_type: this.billType,
                is_balance_due: this.isBalanceDue,
                include_only_invoices: this.includeOnlyInvoices
            });
        this.buttonConfirm.busy = false;
    }

    public get buttonConfirm(): Button {
        return this._buttonConfirm;
    }

    private static getTitle(isBalanceDue: boolean): string {
        return isBalanceDue ? "Reprint Balance Due" : "Reprint Freight Bill";
    }
}
